import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage, ApiError } from '../api';
import type { ProviderCapability, ProviderConfigVersion, ProviderProbeResult } from '../types';
import { Button, Empty, ErrorNotice, Panel, Skeleton, Status } from '../ui';
import { usePanel } from './persona-extras';

const STATUS_LABELS: Record<string, string> = {
  awaiting_credentials: 'بانتظار اعتماد المزود',
  configured_unverified: 'إعداد مفعّل داخليًا — بلا تحقق حي',
  verified: 'متحقق حيًّا',
  revoked: 'الاعتماد مسحوب'
};

const STATUS_TONES: Record<string, 'good' | 'warning' | 'bad' | 'neutral'> = {
  awaiting_credentials: 'warning',
  configured_unverified: 'warning',
  verified: 'good',
  revoked: 'bad'
};

const VERIFICATION_LABELS: Record<string, string> = {
  none: 'لا اختبار بعد',
  mock_loopback: 'اختبار محلي (loopback) — لا يُحتسب تحققًا حيًّا',
  live_provider: 'تحقق حي بالمزود'
};

const STATE_LABELS: Record<string, string> = {
  draft: 'مسودة', validated: 'متحققة بنيويًا', active: 'نشطة', archived: 'مؤرشفة'
};

function capabilityLabel(item: ProviderCapability): string {
  return item.capability;
}

/**
 * Owner provider activation panel. Every control is real: the draft is persisted as
 * a ConfigVersion, validation is network-free, the bounded test runs the real adapter
 * path (locked egress fails before any socket), and activation pins the version the
 * runtime consumes. Nothing here claims a verified live provider.
 */
export function ProviderActivationPanels() {
  const loader = useCallback(async () => api.providerCapabilities(), []);
  const { data, error, loading, reload } = usePanel(loader);
  const [selected, setSelected] = useState('');
  const [versions, setVersions] = useState<ProviderConfigVersion[] | null>(null);
  const [probe, setProbe] = useState<ProviderProbeResult | null>(null);
  const [notice, setNotice] = useState<{ tone: 'good' | 'warning' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState('');
  const [credential, setCredential] = useState('');
  const [model, setModel] = useState('');
  const [retention, setRetention] = useState<'provider_default' | 'no_store'>('provider_default');
  const [store, setStore] = useState(true);
  const [baseUrl, setBaseUrl] = useState('');

  const items = data?.items ?? [];
  const current = items.find((item) => item.capability === selected) ?? null;

  useEffect(() => {
    if (!selected && items.length > 0) setSelected(items[0]!.capability);
  }, [items, selected]);

  useEffect(() => {
    if (!current) return;
    setModel(current.model);
    setRetention(current.retentionPolicy === 'no_store' ? 'no_store' : 'provider_default');
  }, [current?.capability, current?.model, current?.retentionPolicy, current?.configVersion]);

  const loadVersions = useCallback(async () => {
    if (!current) return;
    try {
      const page = await api.providerVersions(current.provider, current.capability);
      setVersions(page.items);
    } catch (caught) {
      setNotice({ tone: 'bad', text: errorMessage(caught) });
    }
  }, [current?.provider, current?.capability]);

  useEffect(() => {
    setVersions(null);
    setProbe(null);
    void loadVersions();
  }, [loadVersions]);

  function reportError(caught: unknown, prefix: string): void {
    if (caught instanceof ApiError && caught.code === 'SENSITIVE_REAUTH_REQUIRED') {
      setNotice({ tone: 'warning', text: 'هذه العملية الحساسة تتطلب إعادة إدخال رمز الدخول (آخر 10 دقائق). أعد الدخول ثم حاول مرة أخرى.' });
      return;
    }
    setNotice({ tone: 'bad', text: prefix + ': ' + errorMessage(caught) });
  }

  async function saveDraft(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!current) return;
    setBusy('draft');
    setNotice(null);
    try {
      const trimmedModel = model.trim();
      const created = await api.createProviderDraft(current.provider, {
        capability: current.capability,
        ...(trimmedModel ? { model: trimmedModel } : {}),
        retentionPolicy: retention,
        store,
        baseUrl: baseUrl.trim() === '' ? null : baseUrl.trim(),
        ...(credential.trim() ? { credential: credential.trim() } : {})
      });
      setCredential('');
      setNotice({
        tone: 'good',
        text: created.credentialFingerprint
          ? 'حُفظ الاعتماد مشفّرًا (بصمة ' + created.credentialFingerprint.slice(0, 12) + ') وأُنشئت مسودة الإصدار ' + created.version + '.'
          : 'أُنشئت مسودة الإصدار ' + created.version + ' دون تغيير الاعتماد.'
      });
      await loadVersions();
      reload();
    } catch (caught) {
      reportError(caught, 'تعذر حفظ المسودة');
    } finally {
      setBusy('');
    }
  }

  async function runValidate(version: number): Promise<void> {
    if (!current) return;
    setBusy('validate-' + version);
    setNotice(null);
    try {
      await api.validateProviderVersion(current.provider, current.capability, version);
      setNotice({ tone: 'good', text: 'الإصدار ' + version + ' متحقق بنيويًا دون أي اتصال شبكي.' });
      await loadVersions();
    } catch (caught) {
      reportError(caught, 'فشل التحقق البنيوي');
    } finally {
      setBusy('');
    }
  }

  async function runActivate(version: number): Promise<void> {
    if (!current) return;
    setBusy('activate-' + version);
    setNotice(null);
    try {
      const activated = await api.activateProviderVersion(current.provider, {
        capability: current.capability,
        version,
        // The revision the owner is looking at is the newest stored version; a newer
        // one written from another tab must produce a conflict instead of a silent overwrite.
        ...(versions && versions.length > 0 ? { expectedRevision: versions[0]!.version } : {})
      });
      setNotice({ tone: 'good', text: activated.note });
      await loadVersions();
      reload();
    } catch (caught) {
      reportError(caught, 'تعذر التفعيل');
      await loadVersions();
      reload();
    } finally {
      setBusy('');
    }
  }

  async function runTest(): Promise<void> {
    if (!current) return;
    setBusy('test');
    setNotice(null);
    setProbe(null);
    try {
      const result = await api.testProviderCapability(current.provider, current.capability);
      setProbe(result);
      setNotice({
        tone: result.liveVerified ? 'good' : 'warning',
        text: result.liveVerified
          ? 'نجح اختبار حقيقي مع المزود.'
          : 'اكتمل الاختبار عبر مسار ' + result.verificationMode + '؛ لم يُحتسب تحققًا حيًّا.'
      });
      reload();
    } catch (caught) {
      reportError(caught, 'تعذر تنفيذ الاختبار');
    } finally {
      setBusy('');
    }
  }

  async function runRevoke(): Promise<void> {
    if (!current) return;
    setBusy('revoke');
    setNotice(null);
    try {
      await api.revokeProviderCredential(current.provider, current.capability);
      setNotice({ tone: 'warning', text: 'سُحب اعتماد ' + current.provider + '؛ توقّف استخدامه فورًا في وقت التشغيل.' });
      reload();
    } catch (caught) {
      reportError(caught, 'تعذر سحب الاعتماد');
    } finally {
      setBusy('');
    }
  }

  if (loading) return <Skeleton />;
  if (error) return <ErrorNotice message={error} onRetry={reload} />;
  if (items.length === 0) return <Empty title="لا توجد قدرات معروضة" />;

  return (
    <>
      <Panel title="قدرات المزودات" intro="الحالة مشتقة من قاعدة البيانات: إعداد نشط + اعتماد مخزّن، لا من ثوابت في الكود.">
        <ul className="provider-list">
          {items.map((item) => (
            <li key={item.capability}>
              <button
                type="button"
                className={item.capability === selected ? 'provider-chip active' : 'provider-chip'}
                onClick={() => setSelected(item.capability)}
              >
                {capabilityLabel(item)}
              </button>
              <Status tone={STATUS_TONES[item.status] ?? 'neutral'}>{STATUS_LABELS[item.status] ?? item.status}</Status>
              <span className="muted">
                {item.provider} · {item.model} · إصدار الإعداد: {item.configVersion ?? 'لا يوجد'} · الاحتفاظ: {item.retentionPolicy ?? 'غير محدد'}
              </span>
            </li>
          ))}
        </ul>
      </Panel>

      {notice ? <Status tone={notice.tone}>{notice.text}</Status> : null}

      {current ? (
        <Panel
          title={'إعداد قدرة: ' + current.capability}
          intro="المسار: مسودة → تحقق بنيوي مجاني → اختبار محدود → تفعيل إصدار داخلي. لا يُجرى أي اتصال خارجي في هذه المرحلة."
        >
          <dl className="provider-facts">
            <dt>الحالة</dt><dd>{STATUS_LABELS[current.status] ?? current.status}</dd>
            <dt>آخر تحقق</dt><dd>{current.lastVerifiedAt ?? VERIFICATION_LABELS[current.verificationMode] ?? 'لا يوجد'}</dd>
            <dt>بصمة الاعتماد</dt><dd>{current.credentialFingerprint ? current.credentialFingerprint.slice(0, 16) : 'لا يوجد اعتماد'}</dd>
            <dt>الشكل السلكي</dt>
            <dd>
              {current.wireShapeVerified ? 'متحقق مقابل التوثيق الرسمي' : current.docsVerificationNote}
            </dd>
          </dl>

          <form className="provider-form" onSubmit={(event) => void saveDraft(event)}>
            <label>النموذج
              <input value={model} onChange={(event) => setModel(event.target.value)} placeholder={current.model} />
            </label>
            <label>سياسة الاحتفاظ
              <select value={retention} onChange={(event) => setRetention(event.target.value === 'no_store' ? 'no_store' : 'provider_default')}>
                <option value="provider_default">سياسة المزود الافتراضية</option>
                <option value="no_store">عدم الحفظ (store=false)</option>
              </select>
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={store} onChange={(event) => setStore(event.target.checked)} />
              السماح بحفظ المزود للطلب
            </label>
            <label className="full">عنوان محلي للاختبار فقط (loopback، اختياري)
              <input value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} placeholder="https://127.0.0.1:9443/v1" />
            </label>
            {current.capability !== 'push' ? (
              <label className="full">اعتماد المزود (يُخزَّن مشفّرًا ولا يُعرض مرة أخرى)
                <input
                  type="password"
                  autoComplete="off"
                  value={credential}
                  onChange={(event) => setCredential(event.target.value)}
                  placeholder="اتركه فارغًا إن لم ترغب في تغييره"
                />
              </label>
            ) : null}
            <Button type="submit" busy={busy === 'draft'}>حفظ مسودة الإعداد</Button>
          </form>

          <div className="provider-actions">
            <Button variant="secondary" busy={busy === 'test'} disabled={!current.canTest} onClick={() => void runTest()}>
              اختبار محدود
            </Button>
            <Button variant="danger" busy={busy === 'revoke'} disabled={!current.credentialFingerprint || current.status === 'revoked'} onClick={() => void runRevoke()}>
              سحب الاعتماد
            </Button>
          </div>
          {current.canTest ? null : <p className="muted">{current.testBlockedReason}</p>}
          {probe ? (
            <p className="muted">
              نتيجة الاختبار: {probe.ok ? 'نجح' : 'لم ينجح'} · الوسم {VERIFICATION_LABELS[probe.verificationMode]} · الزمن {probe.latencyMs}ms ·
              بوابة الخروج {probe.egressMode === 'locked' ? 'مقفلة' : 'مفتوحة'}
            </p>
          ) : null}
        </Panel>
      ) : null}

      {current ? (
        <Panel title="إصدارات الإعداد" intro="التفعيل يثبّت إصدارًا واحدًا لكل قدرة؛ الكتابة القديمة تُرفض بتعارض 409.">
          {versions === null ? <Skeleton /> : versions.length === 0 ? <Empty title="لا توجد إصدارات بعد" /> : (
            <ul className="provider-versions">
              {versions.map((version) => (
                <li key={version.namespace + version.version}>
                  <span>الإصدار {version.version} — {STATE_LABELS[version.state] ?? version.state}</span>
                  <span className="muted">
                    {String(version.document.model ?? '')} · {String(version.document.retentionPolicy ?? '')}
                  </span>
                  <Button variant="secondary" busy={busy === 'validate-' + version.version} disabled={version.state !== 'draft'} onClick={() => void runValidate(version.version)}>
                    تحقق
                  </Button>
                  <Button busy={busy === 'activate-' + version.version} disabled={version.state === 'active' || version.state === 'archived'} onClick={() => void runActivate(version.version)}>
                    تفعيل
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      ) : null}
    </>
  );
}
